import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

type PaymentStatus = 'ok' | 'rejected' | 'aborted' | 'timeout' | 'error';

const CONTENT: Record<PaymentStatus, { icon: string; color: string; title: string; body: string }> = {
  ok: {
    icon: '✅',
    color: '#15803D',
    title: '¡Pago confirmado!',
    body: 'Tu anticipo fue procesado con éxito. Tu reserva está confirmada. La clínica ya fue notificada.',
  },
  rejected: {
    icon: '❌',
    color: '#DC2626',
    title: 'Pago rechazado',
    body: 'Tu tarjeta fue rechazada. Podés intentarlo nuevamente desde el link que recibiste en tu correo.',
  },
  aborted: {
    icon: '↩️',
    color: '#92400E',
    title: 'Pago cancelado',
    body: 'Cancelaste el proceso de pago. Tu reserva sigue pendiente de confirmación.',
  },
  timeout: {
    icon: '⏱️',
    color: '#92400E',
    title: 'Tiempo agotado',
    body: 'El formulario de pago expiró. Tu reserva sigue pendiente. Usá el link del correo para intentarlo nuevamente.',
  },
  error: {
    icon: '⚠️',
    color: '#DC2626',
    title: 'Error inesperado',
    body: 'Ocurrió un problema al procesar el pago. Contactá a la clínica si el problema persiste.',
  },
};

function formatPrice(n: number): string {
  return n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' });
}

function formatFecha(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-CL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Santiago',
    });
  } catch {
    return iso;
  }
}

const PaymentResultPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate  = useNavigate();

  const status     = (params.get('status') ?? 'error') as PaymentStatus;
  const content    = CONTENT[status] ?? CONTENT.error;
  const monto      = params.get('monto') ? Number(params.get('monto')) : null;
  const terapeuta  = params.get('terapeuta') ?? '';
  const fecha      = params.get('fecha') ?? '';
  const consultaId = params.get('consulta_id') ?? '';

  const showDetail = status === 'ok' && (monto || terapeuta || fecha);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F7F9FC',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 16px',
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        padding: '48px 40px',
        maxWidth: 480,
        width: '100%',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>{content.icon}</div>

        <h1 style={{ margin: '0 0 12px', fontSize: 26, fontWeight: 700, color: content.color }}>
          {content.title}
        </h1>

        <p style={{ margin: '0 0 24px', fontSize: 15, color: '#4A5568', lineHeight: 1.7 }}>
          {content.body}
        </p>

        {showDetail && (
          <div style={{
            background: '#F0FDF4',
            border: '1.5px solid #86EFAC',
            borderRadius: 12,
            padding: '20px 24px',
            marginBottom: 28,
            textAlign: 'left',
          }}>
            {monto !== null && (
              <div style={{ marginBottom: 12 }}>
                <p style={{ margin: '0 0 2px', fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Monto pagado
                </p>
                <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#14532D' }}>
                  {formatPrice(monto)}
                </p>
              </div>
            )}
            {(terapeuta || fecha) && (
              <div style={{ borderTop: '1px solid #BBF7D0', paddingTop: 12, display: 'grid', gap: 8 }}>
                {terapeuta && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#15803D', fontWeight: 600 }}>Terapeuta</span>
                    <span style={{ color: '#14532D' }}>{terapeuta}</span>
                  </div>
                )}
                {fecha && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, gap: 12 }}>
                    <span style={{ color: '#15803D', fontWeight: 600, flexShrink: 0 }}>Fecha</span>
                    <span style={{ color: '#14532D', textAlign: 'right' }}>{formatFecha(fecha)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 24 }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: '#1E4A8F',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Volver al inicio
          </button>

          <a
            href="tel:+56978009119"
            style={{
              background: '#EEF4FF',
              color: '#1E4A8F',
              border: 'none',
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            Llamar a la clínica
          </a>
        </div>

        {consultaId && (
          <p style={{ margin: '0 0 8px', fontSize: 11, color: '#CBD5E0', fontFamily: 'monospace' }}>
            Ref: {consultaId}
          </p>
        )}

        <p style={{ margin: 0, fontSize: 12, color: '#94A3B8' }}>
          Conectemos Chile · <a href="mailto:contacto@conectemoschile.cl" style={{ color: '#94A3B8' }}>contacto@conectemoschile.cl</a>
        </p>
      </div>
    </div>
  );
};

export default PaymentResultPage;
